import React from "react";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { API_get_company_feature_settings } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import { FEATURE_BUCKETS, BUCKET_ICONS, formatFlagName, getFlagTooltip } from "../locations_integrations_hours_settings/featureFlagConstants";
import { FEATURE_PRESETS, featureDraftChanges } from "./onboardingFeatureDraft";

export default function OnboardingFeatureEditor({ companyId, pipelineEnabled, onApply, saving, disabled }) {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [applyToExisting, setApplyToExisting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [revision, setRevision] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setData(null); setDraft({}); setError(""); setApplyToExisting(false);
    getRightworkAPI(API_get_company_feature_settings, { company_id: companyId })
      .then(response => { if (active) setData(response?.data?.redux?.payload); })
      .catch(() => { if (active) setError("Could not load feature settings. Please retry."); });
    return () => { active = false; };
  }, [companyId, revision]);
  if (!data) return (
    <div className="mb-3 ml-8 mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:ml-0">
      {error ? (
        <div role="alert" className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Couldn’t load features.</p>
          <button type="button" onClick={() => setRevision(n => n + 1)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">Retry</button>
        </div>
      ) : (
        <div role="status" aria-label="Loading features" aria-busy="true">
          <span className="sr-only">Loading features</span>
          <div aria-hidden="true" className="animate-pulse motion-reduce:animate-none">
            <div className="h-4 w-40 rounded bg-slate-200" />
            <div className="mt-3 h-3 w-2/3 rounded bg-slate-200/70" />
            <div className="mt-4 flex gap-2">
              {[0, 1, 2].map(index => <div key={index} className="h-8 w-1/3 max-w-[160px] rounded-lg bg-slate-200/70" />)}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-1">
              {[0, 1, 2, 3].map(index => (
                <div key={index} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2"><div className="h-5 w-5 rounded bg-slate-200" /><div className="h-3 w-24 rounded bg-slate-200" /></div>
                  <div className="mt-3 h-3 w-3/4 rounded bg-slate-100" />
                  <div className="mt-4 flex items-center justify-between"><div className="h-3 w-20 rounded bg-slate-100" /><div className="h-6 w-11 rounded-full bg-slate-200" /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
  const locked = new Set([...(data.internal_only_flags || []), ...(data.always_enabled_flags || [])]);
  const realStores = data.stores;
  const targetStores = applyToExisting ? data.stores : [];
  const changes = featureDraftChanges(data, draft, applyToExisting);
  const changedFlags = Object.keys(changes);
  const payload = changedFlags.length ? changes : draft;
  const valueFor = flag => draft[flag] ?? data.company_flags[flag];
  const toggle = flag => setDraft(current => ({ ...current, [flag]: !valueFor(flag) }));
  const renderSwitch = flag => {
    if (!Object.prototype.hasOwnProperty.call(data.company_flags, flag)) return null;
    const on = Boolean(valueFor(flag));
    const count = realStores.filter(store => store.flags[flag]).length;
    return <div key={flag} className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="mt-0.5 text-xs text-slate-500">{locked.has(flag) ? (data.always_enabled_flags?.includes(flag) ? "Always enabled" : "Managed by Rightwork in Configurations") : realStores.length ? `${count} of ${realStores.length} locations currently on` : "Default for new locations"}</p>
      </div>
      <button type="button" role="switch" aria-checked={on} aria-label={formatFlagName(flag)} title={getFlagTooltip(flag)}
        disabled={disabled || saving || locked.has(flag)} onClick={() => toggle(flag)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed disabled:opacity-50 ${on ? "bg-violet-600" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "left-0.5 translate-x-5" : "left-0.5"}`} />
      </button>
    </div>;
  };
  return <div id="onboarding-section-starting_modules" className="mb-3 ml-8 mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:ml-0">
    <div className="flex items-start gap-2"><TuneRoundedIcon className="text-violet-700" /><div>
      <h3 className="text-sm font-semibold text-slate-900">Choose your features</h3>
      <p className="mt-1 text-xs text-slate-500">Choose the defaults new locations will start with.</p>
      <a href={`${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/resources/`} target="_blank" rel="noopener noreferrer"
        className="mt-2 inline-flex items-center gap-1 rounded text-xs font-medium text-violet-700 hover:text-violet-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
        Learn about our features <span aria-hidden="true">↗</span><span className="sr-only"> (opens in a new tab)</span>
      </a>
    </div></div>
    <div className="mt-4 flex flex-wrap gap-2" aria-label="Feature presets">
      {FEATURE_PRESETS.map(preset => <button key={preset.key} type="button" disabled={saving || disabled}
        onClick={() => setDraft(current => ({...current, ...Object.fromEntries(Object.entries(preset.flags).filter(([flag]) => flag in data.company_flags && !locked.has(flag)))}))}
        className="rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50">{preset.label}</button>)}
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-1">
      {FEATURE_BUCKETS.filter(bucket => bucket.primaryFlag in data.company_flags).map(bucket => {
        const Icon = BUCKET_ICONS[bucket.key] || TuneRoundedIcon;
        return <section key={bucket.key} className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2"><Icon fontSize="small" className="text-violet-600" /><h4 className="text-sm font-semibold text-slate-900">{bucket.title}</h4></div>
          <p className="mt-1 text-xs text-slate-500">{bucket.tagline}</p>
          {bucket.requiresScheduling && !valueFor("scheduling_enabled") && <p className="mt-1 text-xs text-amber-700">Requires Scheduling to be on.</p>}
          {renderSwitch(bucket.primaryFlag)}
        </section>;
      })}
    </div>
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white" aria-live="polite">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <h4 className="text-sm font-semibold text-slate-900">Review changes</h4>
        {changedFlags.length > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{changedFlags.length} {changedFlags.length === 1 ? "change" : "changes"}</span>}
      </div>
      {changedFlags.length ? (
        <ul className="divide-y divide-slate-100">
          {changedFlags.map(flag => {
            const locations = targetStores.filter(store => store.flags[flag] !== changes[flag]);
            const defaultChanges = data.company_flags[flag] !== changes[flag];
            return <li key={flag} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-slate-800">{formatFlagName(flag)}</span>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${changes[flag] ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                  <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${changes[flag] ? "bg-violet-500" : "bg-slate-400"}`} />
                  {changes[flag] ? "Turn on" : "Turn off"}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                {defaultChanges && <span className="rounded-md bg-violet-50 px-2 py-1 text-violet-700" title="Default for new locations">New locations</span>}
                {locations.map(store => <span key={store.id} className="rounded-md bg-slate-50 px-2 py-1 text-slate-600">{store.store_name}</span>)}
              </div>
            </li>;
          })}
        </ul>
      ) : <p className="px-4 py-3 text-xs text-slate-500">No pending changes.</p>}
      <div className="px-4">
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3">
        {realStores.length > 0 && changedFlags.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 sm:w-full">
            <input type="checkbox" checked={applyToExisting} disabled={saving || disabled}
              onChange={event => setApplyToExisting(event.target.checked)}
              className="h-4 w-4 shrink-0 rounded border-slate-300 accent-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600" />
            Also apply to existing locations ({realStores.length})
          </label>
        )}
        <div className="ml-auto flex flex-wrap justify-end gap-2">
        <button type="button" disabled={saving || disabled || !Object.keys(draft).length} onClick={() => {setDraft({}); setError(""); setApplyToExisting(false);}} className="rounded-lg px-3 py-2 text-sm text-slate-600 disabled:opacity-50">Discard changes</button>
        <button type="button" disabled={saving || disabled || !Object.keys(draft).length} onClick={async () => {
          setError("");
          try { await onApply(payload, applyToExisting); setRevision(n => n + 1); }
          catch (_) { setError("Could not confirm the update. Reload settings to check their current state before retrying."); }
        }} className="rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50">{saving ? "Applying…" : changedFlags.length ? "Apply changes" : "Confirm current choices"}</button>
        </div>
      </div>
    </div>
  </div>;
}
